import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import gameApi from '../services/GameApiService';
import type { FinalEvaluation, PlayerState } from '../services/GameApiService';
import { saveSession, savePlayerEmail, clearSession } from '../utils/sessionPersistence';
import { useSfx } from '../audio';

// sessionStorage flag partilhado com a WaitingPage — se o jogador dispensou o
// prompt de email, não voltamos a chatear na mesma sessão de browser.
const EMAIL_PROMPT_DISMISSED_KEY = 'selna_email_prompt_dismissed';

type PageState = 'loading' | 'ready' | 'submitting' | 'submitted' | 'restarting' | 'error';

interface CompletionData {
  adventureId: string;
  adventureTitle: string;
  episodesCompleted: number;
  finalEvaluation: FinalEvaluation | null;
  completedAt: string;
  hasSubmittedHighscore: boolean;
  playerState: PlayerState;
}

export default function GameCompletePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // 🔊 One-shot SFX (Fase 0). unlock() is the first user gesture here: the
  // "Submeter highscore" / "Voltar ao início" buttons. Until then play() is
  // a safe no-op.
  const sfx = useSfx();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState('');
  const [data, setData] = useState<CompletionData | null>(null);
  const [playerName, setPlayerName] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submittedHighscore, setSubmittedHighscore] = useState<{ rank?: number; playerName: string } | null>(null);
  const [restartError, setRestartError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // ── Segunda oportunidade de email (F4b) ────────────────────────────────
  // Se o jogador dispensou no fim do EP2 (sessionStorage flag), não voltamos
  // a chatear. Só aparece se o playerState ainda não tem email.
  const [resultEmail, setResultEmail] = useState('');
  const [resultEmailError, setResultEmailError] = useState('');
  const [isAttachingResultEmail, setIsAttachingResultEmail] = useState(false);
  const [resultEmailAttached, setResultEmailAttached] = useState(false);
  const [resultEmailDismissed] = useState(
    () => sessionStorage.getItem(EMAIL_PROMPT_DISMISSED_KEY) === '1',
  );

  const handleAttachResultEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !resultEmail.trim()) return;
    setIsAttachingResultEmail(true);
    setResultEmailError('');
    try {
      const result = await gameApi.attachEmail(token, resultEmail.trim());
      if (result.status === 'attached') {
        setResultEmailAttached(true);
      } else if (result.status === 'conflict') {
        // Conflito no ecrã final: o jogador já tem um run completo noutro
        // device com este email. Não faz sentido abrir o modal de switch
        // aqui (este run já está completo) — informamos e ficamos por aqui.
        setResultEmailError('Já existe uma aventura associada a este email noutro dispositivo. Usa esse dispositivo para a consultar.');
      }
    } catch (err) {
      setResultEmailError(err instanceof Error ? err.message : 'Falha ao anexar email');
    } finally {
      setIsAttachingResultEmail(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    loadCompletionData();
  }, [token]);

  const loadCompletionData = async () => {
    if (!token) return;

    try {
      const completionData = await gameApi.getCompletionData(token);
      setData(completionData);
      
      if (completionData.hasSubmittedHighscore) {
        setPageState('submitted');
        setSubmittedHighscore({ playerName: completionData.playerState.playerName || '' });
      } else {
        // Pre-fill player name if they've submitted a highscore before (from a previous adventure)
        if (completionData.playerState.playerName) {
          setPlayerName(completionData.playerState.playerName);
        }
        setPageState('ready');
      }
      setRetryCount(0); // Reset retry count on success

      // 🔊 Adventure complete! Triadic arpeggio. Safe no-op if muted / not
      // yet unlocked (this loads on page mount, before any user gesture).
      sfx.play('episode_complete');

      saveSession({
        token: token!,
        adventureTitle: completionData.adventureTitle,
        currentEpisode: completionData.episodesCompleted,
        totalEpisodes: completionData.episodesCompleted,
        lastPage: 'complete',
        playerStatus: 'completed',
      });
      if (completionData.playerState.email) {
        savePlayerEmail(completionData.playerState.email);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar dados';
      
      // If it's a "Failed to fetch" error and we haven't exceeded retries, try again
      if (errorMessage.includes('Failed to fetch') && retryCount < maxRetries) {
        console.log(`[GameCompletePage] Network error, retry ${retryCount + 1}/${maxRetries}`);
        setRetryCount(retryCount + 1);
        // Retry after a delay (exponential backoff: 1s, 2s, 4s)
        setTimeout(() => {
          loadCompletionData();
        }, Math.pow(2, retryCount) * 1000);
      } else {
        // Either not a network error, or we've exceeded retries
        setError(errorMessage);
        setPageState('error');
      }
    }
  };

  const handleSubmitHighscore = async () => {
    if (!token || !playerName.trim()) {
      setSubmitError('Por favor, insere o teu nome');
      return;
    }

    setPageState('submitting');
    setSubmitError('');

    try {
      const result = await gameApi.submitHighscore(token, playerName.trim());
      setSubmittedHighscore({ 
        playerName: result.highscore.playerName,
        rank: result.highscore.rank
      });
      setPageState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Falha ao submeter');
      setPageState('ready');
    }
  };

  const handleRestart = async () => {
    if (!token) return;

    setPageState('restarting');
    setRestartError('');

    try {
      const result = await gameApi.restartAdventure(token);
      // Clear old session — EpisodePage will save the new one
      clearSession();
      // Navigate to the episode page with the new token
      navigate(`/play/${result.token}`);
    } catch (err) {
      setRestartError(err instanceof Error ? err.message : 'Falha ao reiniciar');
      setPageState('submitted');
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="A carregar a tua avaliação..." />
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-dark mb-4">
            Oops! Algo correu mal
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a 
            href="/" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 transition-colors"
          >
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const evaluation = data.finalEvaluation;
  const score = evaluation?.score ?? 0;

  // Determine score tier for display
  const getScoreTier = (score: number) => {
    if (score >= 80) return { label: 'Excelente', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (score >= 60) return { label: 'Bom', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    if (score >= 40) return { label: 'Razoável', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    return { label: 'A melhorar', color: 'text-orange-600', bgColor: 'bg-orange-100' };
  };

  const tier = getScoreTier(score);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold text-dark">
            Aventura Completa!
          </h1>
          <p className="text-gray-600 mt-2">
            {data.adventureTitle}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {data.episodesCompleted} episódios completados
          </p>
        </div>

        {/* Score Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Pontuação Final
            </h2>
            
            {/* Score Circle */}
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="url(#scoreGradient)"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 352} 352`}
                />
                <defs>
                  <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#E96A61" />
                    <stop offset="100%" stopColor="#AC8DC4" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-dark">{score}</span>
              </div>
            </div>

            <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${tier.bgColor} ${tier.color}`}>
              {tier.label}
            </span>
          </div>

          {/* Score Breakdown */}
          {evaluation?.breakdown && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Competências Avaliadas</h3>
              <div className="space-y-3">
                {/* Communication */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Comunicação Eficaz</span>
                    <span className="font-medium">{evaluation.breakdown.communicationScore}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all"
                      style={{ width: `${evaluation.breakdown.communicationScore}%` }}
                    />
                  </div>
                </div>
                {/* Context Adaptation */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Adaptação ao Contexto</span>
                    <span className="font-medium">{evaluation.breakdown.contextAdaptationScore}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all"
                      style={{ width: `${evaluation.breakdown.contextAdaptationScore}%` }}
                    />
                  </div>
                </div>
                {/* Goal Achievement */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Objetivos Atingidos</span>
                    <span className="font-medium">{evaluation.breakdown.goalAchievementScore}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all"
                      style={{ width: `${evaluation.breakdown.goalAchievementScore}%` }}
                    />
                  </div>
                </div>
                {/* Emotional Management */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Gestão Emocional</span>
                    <span className="font-medium">{evaluation.breakdown.emotionalManagementScore}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all"
                      style={{ width: `${evaluation.breakdown.emotionalManagementScore}%` }}
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Avaliação independente baseada na qualidade da comunicação, não nas reações dos personagens.
              </p>
            </div>
          )}
        </div>

        {/* Written Evaluation */}
        {evaluation?.evaluation && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-dark mb-4">
              Avaliação
            </h2>
            
            {/* Overall Assessment */}
            <div className="prose prose-sm max-w-none mb-6">
              <p className="text-gray-700 whitespace-pre-wrap">
                {evaluation.evaluation.overallAssessment}
              </p>
            </div>

            {/* Strengths */}
            {evaluation.evaluation.strengths.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center">
                  <span className="mr-2">💪</span> Pontos Fortes
                </h3>
                <ul className="space-y-1">
                  {evaluation.evaluation.strengths.map((strength, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Areas to Improve */}
            {evaluation.evaluation.areasToImprove.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center">
                  <span className="mr-2">🎯</span> Áreas a Melhorar
                </h3>
                <ul className="space-y-1">
                  {evaluation.evaluation.areasToImprove.map((area, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <span className="text-amber-500 mr-2">•</span>
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Communication Style */}
            {evaluation.evaluation.communicationStyle && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center">
                  <span className="mr-2">💬</span> Estilo de Comunicação
                </h3>
                <p className="text-sm text-gray-700">
                  {evaluation.evaluation.communicationStyle}
                </p>
              </div>
            )}

            {/* Memorable Moment */}
            {evaluation.evaluation.memorableMoment && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-purple-700 mb-2 flex items-center">
                  <span className="mr-2">⭐</span> Momento Memorável
                </h3>
                <p className="text-sm text-gray-700">
                  {evaluation.evaluation.memorableMoment}
                </p>
              </div>
            )}

            {/* Advice for Next */}
            {evaluation.evaluation.adviceForNext && (
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-4 mt-4">
                <h3 className="text-sm font-semibold text-dark mb-2 flex items-center">
                  <span className="mr-2">💡</span> Conselho para a Próxima Aventura
                </h3>
                <p className="text-sm text-gray-700">
                  {evaluation.evaluation.adviceForNext}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Highscore Submission */}
        {pageState === 'ready' && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-dark mb-4 text-center">
              Entra no Ranking!
            </h2>
            <p className="text-sm text-gray-600 text-center mb-4">
              Introduz o teu nome para aparecer no ranking de jogadores.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value.slice(0, 20))}
                placeholder="O teu nome (max 20 caracteres)"
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50"
                maxLength={20}
              />
              <button
                onClick={handleSubmitHighscore}
                disabled={!playerName.trim()}
                className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white font-medium rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submeter
              </button>
            </div>
            
            {submitError && (
              <p className="text-red-500 text-sm mt-2 text-center">{submitError}</p>
            )}
          </div>
        )}

        {/* Submitting State */}
        {pageState === 'submitting' && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 text-center">
            <LoadingSpinner size="md" message="A submeter..." />
          </div>
        )}

        {/* Submitted State */}
        {pageState === 'submitted' && submittedHighscore && (
          <div className="bg-green-50 rounded-2xl p-6 mb-6 text-center">
            <div className="text-4xl mb-2">🏆</div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Submetido com Sucesso!
            </h3>
            <p className="text-green-700">
              <span className="font-medium">{submittedHighscore.playerName}</span> foi adicionado ao ranking.
            </p>
          </div>
        )}

        {/* Segunda oportunidade de email (F4b) — gancho "guardar o resultado".
         * Só aparece se o jogador ainda não tem email e não dispensou na
         * sessão actual. Nunca em episódios intermédios — isto é o ecrã final. */}
        {data && !data.playerState?.email && !resultEmailAttached && !resultEmailDismissed && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-dark mb-2">
              Guardar o teu resultado
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Mete o email para sincronizar este resultado noutro dispositivo.
              Só te enviamos novidades do jogo — nunca spam.
            </p>
            <form onSubmit={handleAttachResultEmail} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={resultEmail}
                onChange={(e) => setResultEmail(e.target.value)}
                placeholder="o.teu@email.com"
                required
                disabled={isAttachingResultEmail}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isAttachingResultEmail || !resultEmail.trim()}
                className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isAttachingResultEmail ? 'A guardar...' : 'Guardar'}
              </button>
            </form>
            {resultEmailError && (
              <p className="text-red-500 text-sm mt-2">{resultEmailError}</p>
            )}
          </div>
        )}

        {/* Confirmação leve após attach com sucesso no ecrã final */}
        {resultEmailAttached && (
          <div className="bg-green-50 rounded-2xl p-4 mb-6 text-center">
            <p className="text-green-700 text-sm font-medium">
              ✓ Sincronização ativada. Enviámos um link mágico para o teu email.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/highscores"
            className="px-6 py-3 bg-white border border-gray-200 text-dark font-medium rounded-xl hover:bg-gray-50 transition-colors text-center"
          >
            Ver Ranking
          </Link>
          <button
            onClick={handleRestart}
            disabled={pageState === 'restarting'}
            className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white font-medium rounded-xl hover:shadow-lg transition-all text-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {pageState === 'restarting' ? 'A reiniciar...' : 'Reiniciar Aventura'}
          </button>
        </div>
        {restartError && (
          <p className="text-red-500 text-sm mt-3 text-center">{restartError}</p>
        )}
      </div>
    </div>
  );
}
